// Vollea — Create-League bottom-sheet modal.
// Used by Club Admin ("Create League") + League Organiser ("Create Independent League").
import React, { useEffect, useState } from "react";
import { IoClose, IoRemove, IoAdd } from "react-icons/io5";
import { Btn, Avatar } from "./ui-vollea";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function CreateLeagueModal({ mode = "club", clubId, onClose, onCreated }) {
  const independent = mode === "organiser";
  const title = independent ? "Create Independent League" : "Create League";

  const [name, setName] = useState("");
  const [n, setN] = useState(2);
  const [assignable, setAssignable] = useState([]);
  const [runBy, setRunBy] = useState(null); // user_id of selected organiser (null = self)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgOk, setMsgOk] = useState(false);

  useEffect(() => {
    if (independent || !clubId) return;
    api.get(`/clubs/${clubId}/assignable-organisers`).then((rows) => {
      setAssignable(rows || []);
      const self = (rows || []).find((r) => r.is_self);
      setRunBy(self ? self.user_id : null);
    }).catch(() => setAssignable([]));
  }, [independent, clubId]);

  const dec = () => setN((v) => Math.max(1, v - 1));
  const inc = () => setN((v) => Math.min(6, v + 1));

  const helper = n === 1
    ? "Single division — all players compete in one combined table."
    : `${n} divisions (Division 1–${n}) — players are grouped by skill tier.`;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true); setMsg(""); setMsgOk(false);
    try {
      const divisions = Array.from({ length: n }, (_, i) => `Division ${i + 1}`);
      const body = { name: name.trim(), divisions };
      if (!independent && runBy) {
        const self = assignable.find((a) => a.is_self);
        if (!self || runBy !== self.user_id) body.organiser_id = runBy;
      }
      const lg = await api.post("/leagues", body);
      setMsgOk(true);
      setMsg("League created!");
      setName("");
      setN(2);
      onCreated?.(lg);
      setTimeout(onClose, 700);
    } catch (e) { setMsgOk(false); setMsg(e.message || "Could not create league"); }
    finally { setBusy(false); }
  };

  const label = { color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase", display: "block" };
  const input = { width: "100%", background: C.surface2, borderRadius: R.md, border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`, height: 52, fontSize: 15, outline: "none" };

  const showRunBy = !independent && assignable.length > 1;

  return (
    <div
      role="dialog"
      data-testid="create-league-modal"
      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, background: C.surface, borderTop: `1px solid ${C.border}`,
          borderRadius: `${R.lg}px ${R.lg}px 0 0`, padding: `${SP.lg}px ${SP.lg}px 40px`,
          maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.lg }}>
          <h2 style={{ color: C.text, fontWeight: 700, fontSize: 22, margin: 0, letterSpacing: 0.4 }}>{title}</h2>
          <button type="button" onClick={onClose} data-testid="create-league-close" style={{ color: C.textMuted, padding: 4 }}>
            <IoClose size={26} />
          </button>
        </div>

        <label style={label}>League name</label>
        <input
          data-testid="create-league-name"
          style={input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Autumn 2026 Mixed"
          maxLength={60}
          autoFocus
          required
        />

        <div style={{ marginTop: SP.lg }}>
          <div style={{ ...label, marginBottom: SP.sm }}>Divisions</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP.md, background: C.surface2, borderRadius: R.md, border: `1px solid ${C.border}`, padding: SP.md }}>
            <button
              type="button"
              onClick={dec}
              disabled={n <= 1}
              data-testid="create-league-div-dec"
              style={{ width: 44, height: 44, borderRadius: 22, background: C.surface3, color: C.text, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: n <= 1 ? 0.35 : 1 }}
            >
              <IoRemove size={20} />
            </button>
            <div style={{ textAlign: "center" }}>
              <div data-testid="create-league-div-value" style={{ color: C.lime, fontWeight: 700, fontSize: 36, lineHeight: 1 }}>{n}</div>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1, marginTop: 4, textTransform: "uppercase" }}>
                {n === 1 ? "division" : "divisions"}
              </div>
            </div>
            <button
              type="button"
              onClick={inc}
              disabled={n >= 6}
              data-testid="create-league-div-inc"
              style={{ width: 44, height: 44, borderRadius: 22, background: C.surface3, color: C.text, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: n >= 6 ? 0.35 : 1 }}
            >
              <IoAdd size={20} />
            </button>
          </div>
          <p data-testid="create-league-div-helper" style={{ color: C.textMuted, fontSize: 12, margin: `${SP.sm}px 0 0`, lineHeight: 1.4 }}>{helper}</p>
        </div>

        {showRunBy && (
          <div style={{ marginTop: SP.lg }}>
            <div style={{ ...label, marginBottom: SP.sm }}>Run by</div>
            <div data-testid="create-league-runby" style={{ display: "flex", gap: SP.sm, flexWrap: "wrap" }}>
              {assignable.map((a) => {
                const active = runBy === a.user_id;
                return (
                  <button
                    type="button"
                    key={a.user_id}
                    onClick={() => setRunBy(a.user_id)}
                    data-testid={`create-league-runby-${a.is_self ? "self" : a.user_id}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      height: 40, padding: `0 12px`, borderRadius: R.pill,
                      background: active ? C.lime : C.surface2,
                      border: `1px solid ${active ? C.lime : C.border}`,
                      color: active ? C.onLime : C.text,
                      fontWeight: 600, fontSize: 13,
                    }}
                  >
                    <Avatar uri={a.photo} name={a.name} size={24} />
                    {a.is_self ? "You" : a.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {msg ? (
          <div
            data-testid="create-league-msg"
            style={{
              marginTop: SP.lg,
              background: msgOk ? C.limeTint : "rgba(239,68,68,0.1)",
              border: `1px solid ${msgOk ? C.lime : C.error}`,
              color: msgOk ? C.lime : C.error,
              padding: SP.md, borderRadius: R.md, fontSize: 13, fontWeight: 600,
            }}
          >{msg}</div>
        ) : null}

        <p style={{ color: C.textDim, fontSize: 12, margin: `${SP.lg}px 0 ${SP.md}px`, textAlign: "center" }}>
          A unique join code is generated automatically.
        </p>
        <Btn label="Create" loading={busy} type="submit" testid="create-league-submit" />
      </form>
    </div>
  );
}
